/**
 * Match Flow Hook
 *
 * Manages the match flow state and socket communication.
 */

import type {
  MatchFlowCompletedEvent,
  MatchFlowFailedEvent,
  MatchFlowNodeCompletedEvent,
  MatchFlowNodeErrorEvent,
  MatchFlowNodeStartedEvent,
  MatchFlowNodeTimeoutEvent,
  MatchFlowNodeUpdateEvent,
  MatchFlowPlayerDisconnectedEvent,
  MatchFlowPlayerReconnectedEvent,
  MatchFlowStartedEvent,
  MatchFlowState,
  MatchFlowStateSyncEvent,
  NodeState,
  PlayerAction,
} from "@rivaled/shared";
import { MATCH_FLOW_EVENTS } from "@rivaled/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { getSocket } from "../../lib/socket";

type UseMatchFlowOptions = {
  matchId: string;
  enabled?: boolean;
};

type UseMatchFlowResult = {
  // State
  flowState: MatchFlowState | null;
  currentNode: NodeState | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;

  // Computed
  timeRemaining: number | null;
  progress: { completed: number; total: number };

  // Actions
  submitAction: (
    actionType: string,
    payload: unknown,
  ) => Promise<{ success: boolean; error?: string }>;
  refresh: () => void;
};

export function useMatchFlow({ matchId, enabled = true }: UseMatchFlowOptions): UseMatchFlowResult {
  const [flowState, setFlowState] = useState<MatchFlowState | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // Join flow room and get initial state
  const joinTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const joinFlow = useCallback(() => {
    if (!enabled) return;

    const socket = getSocket();

    // Don't emit on a disconnected socket — will retry on reconnect
    if (!socket.connected) return;

    setIsLoading(true);
    setError(null);

    // Timeout if server doesn't respond within 15 seconds
    if (joinTimeoutRef.current) clearTimeout(joinTimeoutRef.current);
    joinTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setError("Connection timed out — try refreshing the page");
    }, 15_000);

    socket.emit(MATCH_FLOW_EVENTS.JOIN, { matchId }, (state: MatchFlowState | null) => {
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }
      setFlowState(state);
      setIsLoading(false);

      if (!state) {
        setError("Flow not found");
      }
    });
  }, [matchId, enabled]);

  // Leave flow room
  const leaveFlow = useCallback(() => {
    const socket = getSocket();
    socket.emit(MATCH_FLOW_EVENTS.LEAVE, { matchId });
  }, [matchId]);

  // Submit an action
  const submitAction = useCallback(
    async (actionType: string, payload: unknown): Promise<{ success: boolean; error?: string }> => {
      return new Promise((resolve) => {
        const socket = getSocket();

        if (!flowState?.currentNodeId) {
          resolve({ success: false, error: "No active node" });
          return;
        }

        if (!socket.connected) {
          resolve({ success: false, error: "Disconnected — please retry" });
          return;
        }

        const timeout = setTimeout(() => {
          toast.error("Connection issue — try again");
          resolve({ success: false, error: "Action timed out — please try again" });
        }, 10_000);

        const action: PlayerAction = { type: actionType, payload };

        socket.emit(
          MATCH_FLOW_EVENTS.ACTION,
          { matchId, nodeId: flowState.currentNodeId, action },
          (response: { success: boolean; error?: string }) => {
            clearTimeout(timeout);
            resolve(response);
          },
        );
      });
    },
    [matchId, flowState?.currentNodeId],
  );

  // Set up socket event handlers
  useEffect(() => {
    if (!enabled) return;

    const socket = getSocket();

    const handleConnect = () => {
      setIsConnected(true);
      // Re-join the flow room on (re)connect so we get fresh state
      joinFlow();
    };
    const handleDisconnect = () => setIsConnected(false);

    const handleNodeStarted = (event: MatchFlowNodeStartedEvent) => {
      if (event.matchId !== matchId) return;

      // Clear any error from the previous node
      setError(null);

      setFlowState((prev) => {
        if (!prev) return prev;

        const nodeState: NodeState = {
          nodeId: event.nodeId,
          nodeType: event.nodeType,
          status: "active",
          config: event.config,
          state: event.state,
          startedAt: event.startedAt,
          completedAt: null,
          timeoutAt: event.timeoutAt,
          error: null,
        };

        return {
          ...prev,
          currentNodeId: event.nodeId,
          nodeStates: { ...prev.nodeStates, [event.nodeId]: nodeState },
          updatedAt: Date.now(),
        };
      });
    };

    const handleNodeUpdate = (event: MatchFlowNodeUpdateEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        const existing = prev.nodeStates[event.nodeId];
        if (!existing) return prev;

        return {
          ...prev,
          nodeStates: {
            ...prev.nodeStates,
            [event.nodeId]: {
              ...existing,
              state: event.state,
            },
          },
          updatedAt: event.updatedAt,
        };
      });
    };

    const handleNodeCompleted = (event: MatchFlowNodeCompletedEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        const existing = prev.nodeStates[event.nodeId];
        if (!existing) return prev;

        return {
          ...prev,
          // Merge node result into flow context so downstream UI stays up-to-date
          context: { ...prev.context, ...event.result },
          nodeStates: {
            ...prev.nodeStates,
            [event.nodeId]: {
              ...existing,
              status: "completed",
              completedAt: event.completedAt,
            },
          },
          completedNodeIds: [...prev.completedNodeIds, event.nodeId],
          updatedAt: Date.now(),
        };
      });
    };

    const handleNodeTimeout = (event: MatchFlowNodeTimeoutEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        const existing = prev.nodeStates[event.nodeId];
        if (!existing) return prev;

        return {
          ...prev,
          nodeStates: {
            ...prev.nodeStates,
            [event.nodeId]: {
              ...existing,
              status: "timeout",
              completedAt: event.timedOutAt,
            },
          },
          completedNodeIds: [...prev.completedNodeIds, event.nodeId],
          updatedAt: Date.now(),
        };
      });
    };

    const handleCompleted = (event: MatchFlowCompletedEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          status: "completed",
          currentNodeId: null,
          context: { ...prev.context, ...event.context },
          updatedAt: event.completedAt,
        };
      });
    };

    const handleFailed = (event: MatchFlowFailedEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          status: "failed",
          currentNodeId: null,
          updatedAt: event.failedAt,
        };
      });

      setError(event.error);
    };

    const handleStarted = (event: MatchFlowStartedEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          status: "running",
          players: event.players,
          updatedAt: event.startedAt,
        };
      });

      setIsLoading(false);
    };

    const handleStateSync = (event: MatchFlowStateSyncEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState(event.state);
      setIsLoading(false);
      setError(null);
    };

    const handleNodeError = (event: MatchFlowNodeErrorEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        const existing = prev.nodeStates[event.nodeId];
        if (!existing) return prev;

        return {
          ...prev,
          nodeStates: {
            ...prev.nodeStates,
            [event.nodeId]: {
              ...existing,
              status: "error",
              error: event.error,
            },
          },
          updatedAt: event.errorAt,
        };
      });

      setError(event.error);
    };

    const handlePlayerDisconnected = (event: MatchFlowPlayerDisconnectedEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        const player = prev.players.find((p) => p.userId === event.userId);
        if (player) {
          toast.error(`${player.username} disconnected`);
        }

        const players = prev.players.map((p) =>
          p.userId === event.userId
            ? { ...p, isConnected: false, lastSeenAt: event.disconnectedAt }
            : p,
        );

        return { ...prev, players, updatedAt: Date.now() };
      });
    };

    const handlePlayerReconnected = (event: MatchFlowPlayerReconnectedEvent) => {
      if (event.matchId !== matchId) return;

      setFlowState((prev) => {
        if (!prev) return prev;

        const player = prev.players.find((p) => p.userId === event.userId);
        if (player) {
          toast.success(`${player.username} reconnected`);
        }

        const players = prev.players.map((p) =>
          p.userId === event.userId
            ? { ...p, isConnected: true, lastSeenAt: event.reconnectedAt }
            : p,
        );

        return { ...prev, players, updatedAt: Date.now() };
      });
    };

    // Register handlers
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on(MATCH_FLOW_EVENTS.STARTED, handleStarted);
    socket.on(MATCH_FLOW_EVENTS.NODE_STARTED, handleNodeStarted);
    socket.on(MATCH_FLOW_EVENTS.NODE_UPDATE, handleNodeUpdate);
    socket.on(MATCH_FLOW_EVENTS.NODE_COMPLETED, handleNodeCompleted);
    socket.on(MATCH_FLOW_EVENTS.NODE_TIMEOUT, handleNodeTimeout);
    socket.on(MATCH_FLOW_EVENTS.NODE_ERROR, handleNodeError);
    socket.on(MATCH_FLOW_EVENTS.COMPLETED, handleCompleted);
    socket.on(MATCH_FLOW_EVENTS.FAILED, handleFailed);
    socket.on(MATCH_FLOW_EVENTS.STATE_SYNC, handleStateSync);
    socket.on(MATCH_FLOW_EVENTS.PLAYER_DISCONNECTED, handlePlayerDisconnected);
    socket.on(MATCH_FLOW_EVENTS.PLAYER_RECONNECTED, handlePlayerReconnected);

    // Join flow
    joinFlow();

    // Update connection state
    setIsConnected(socket.connected);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off(MATCH_FLOW_EVENTS.STARTED, handleStarted);
      socket.off(MATCH_FLOW_EVENTS.NODE_STARTED, handleNodeStarted);
      socket.off(MATCH_FLOW_EVENTS.NODE_UPDATE, handleNodeUpdate);
      socket.off(MATCH_FLOW_EVENTS.NODE_COMPLETED, handleNodeCompleted);
      socket.off(MATCH_FLOW_EVENTS.NODE_TIMEOUT, handleNodeTimeout);
      socket.off(MATCH_FLOW_EVENTS.NODE_ERROR, handleNodeError);
      socket.off(MATCH_FLOW_EVENTS.COMPLETED, handleCompleted);
      socket.off(MATCH_FLOW_EVENTS.FAILED, handleFailed);
      socket.off(MATCH_FLOW_EVENTS.STATE_SYNC, handleStateSync);
      socket.off(MATCH_FLOW_EVENTS.PLAYER_DISCONNECTED, handlePlayerDisconnected);
      socket.off(MATCH_FLOW_EVENTS.PLAYER_RECONNECTED, handlePlayerReconnected);

      leaveFlow();

      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }
    };
  }, [matchId, enabled, joinFlow, leaveFlow]);

  // Calculate time remaining for current node
  // Use a stable primitive dependency (timeoutAt number) instead of the nodeStates object
  // to avoid re-creating the interval on every state update
  const currentTimeoutAt = flowState?.currentNodeId
    ? (flowState.nodeStates[flowState.currentNodeId]?.timeoutAt ?? null)
    : null;

  useEffect(() => {
    if (!currentTimeoutAt) {
      setTimeRemaining(null);
      return;
    }

    const updateTimeRemaining = () => {
      const remaining = Math.max(0, currentTimeoutAt - Date.now());
      setTimeRemaining(remaining);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [currentTimeoutAt]);

  // Current node
  const currentNode = useMemo(() => {
    if (!flowState?.currentNodeId) return null;
    return flowState.nodeStates[flowState.currentNodeId] ?? null;
  }, [flowState?.currentNodeId, flowState?.nodeStates]);

  // Progress
  const progress = useMemo(() => {
    if (!flowState) return { completed: 0, total: 0 };

    const completed = flowState.completedNodeIds.length;
    // Use totalNodeCount from backend (flow definition node count) if available,
    // otherwise fall back to counting nodes we've seen so far
    const total = flowState.totalNodeCount || Object.keys(flowState.nodeStates).length;

    return { completed, total };
  }, [flowState?.totalNodeCount, flowState?.nodeStates, flowState?.completedNodeIds, flowState]);

  return {
    flowState,
    currentNode,
    isConnected,
    isLoading,
    error,
    timeRemaining,
    progress,
    submitAction,
    refresh: joinFlow,
  };
}
